/**
 * R3 — Run-detail cost card.
 *
 * Three rows: Narration (LLM), Voiceover (TTS), Total.
 * Footnote surfaces the proxy-rate disclaimer iff the narration's rate
 * was marked `proxy=true` (today: GPT-5.2 via Emergent LLM key).
 *
 * Read-only — gated by the standard `presenter+` role server-side.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Info, Loader2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";

function fmt(amount) {
  const a = Number(amount || 0);
  return `$${a.toFixed(4)}`;
}

export default function RunCostCard({ runId }) {
  const q = useQuery({
    queryKey: ["run-cost", runId],
    queryFn: async () => (await api.get(`/runs/${runId}/cost`)).data,
    enabled: !!runId,
    staleTime: 30_000,
  });

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="run-cost-card"
    >
      <header className="mb-3 flex items-center gap-2">
        <DollarSign size={16} className="text-emerald-600" />
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">
          Cost
        </h2>
      </header>

      {q.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
          <Loader2 size={14} className="animate-spin" />
          Loading cost…
        </div>
      ) : q.isError ? (
        <p className="text-xs text-rose-600" data-testid="run-cost-error">
          {formatApiError(q.error)}
        </p>
      ) : (
        <CostBody data={q.data} />
      )}
    </section>
  );
}

function CostBody({ data }) {
  const narration = data?.narration;
  const voiceover = data?.voiceover;
  const isProxy = Boolean(narration?.is_proxy_rate);
  return (
    <>
      <dl className="space-y-1.5 text-sm">
        <CostRow
          label="Narration"
          amount={narration?.total_cost_usd}
          present={!!narration}
          tooltipLabel="LLM token usage"
          tooltipDetail={
            narration
              ? `${narration.model || "—"} · ${
                  narration.prompt_tokens ?? "?"
                } prompt + ${narration.completion_tokens ?? "?"} completion tokens`
              : "No narration generated yet"
          }
          testId="run-cost-narration"
        />
        <CostRow
          label="Voiceover"
          amount={voiceover?.cost_usd}
          present={!!voiceover}
          tooltipLabel="TTS character usage"
          tooltipDetail={
            voiceover
              ? `${voiceover.provider || "—"} · ${voiceover.voice_id || "—"} · ${
                  voiceover.character_count ?? 0
                } chars`
              : "No voiceover generated yet"
          }
          testId="run-cost-voiceover"
        />
        <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
          <span className="text-sm font-semibold text-slate-900">Total</span>
          <span
            className="font-mono text-base font-bold text-slate-900"
            data-testid="run-cost-total"
          >
            {fmt(data?.total_usd)}
          </span>
        </div>
      </dl>
      {isProxy && (
        <p
          className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-slate-500"
          data-testid="run-cost-proxy-footnote"
        >
          <Info size={11} className="mt-0.5 shrink-0 text-slate-400" />
          <span>
            Narration cost estimated via OpenAI rate card (Emergent LLM key
            passthrough).
          </span>
        </p>
      )}
    </>
  );
}

function CostRow({ label, amount, present, tooltipLabel, tooltipDetail, testId }) {
  // Native title attribute — RunCostCard is mounted on the Run Detail
  // page which doesn't wrap its tree in TooltipProvider, so shadcn
  // <Tooltip /> would crash here.  The native tooltip is sufficient for
  // disclosing token/character counts on hover.
  const titleText = `${tooltipLabel} — ${tooltipDetail}`;
  return (
    <div className="flex items-center justify-between" title={titleText}>
      <span className="inline-flex items-center gap-1 text-slate-600">
        {label}
        <Info size={10} className="text-slate-300" />
      </span>
      <span
        className={`font-mono ${present ? "text-slate-900" : "text-slate-300"}`}
        data-testid={testId}
      >
        {present ? fmt(amount) : "—"}
      </span>
    </div>
  );
}
